import { IconButton, InputAdornment } from "@mui/material";
import {
  gridQuickFilterValuesSelector,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";

interface CustomToolbarProps {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // ... props อื่นๆ
}

export const CustomGridToolbarQuickFilter = ({
  value,
  onChange,
  ...other
}: CustomToolbarProps) => {
  const apiRef = useGridApiContext();
  const quickFilterValues = useGridSelector(
    apiRef,
    gridQuickFilterValuesSelector,
  );

  // ✅ 1. ใช้ Local State เก็บค่าใน Input เพื่อรักษา Space ไว้
  // ถ้ามี prop value (Controlled) ให้ใช้ค่า initial จาก value, ถ้าไม่มีให้ว่างไว้ก่อน
  const [searchText, setSearchText] = useState(value ?? "");

  // State สำหรับ Animation
  const [inputOpen, setInputOpen] = useState<boolean>(!!searchText);

  // ✅ 2. Sync ค่าจาก Prop value (กรณี Parent สั่งเปลี่ยนค่า)
  useEffect(() => {
    if (value !== undefined) {
      setSearchText(value);
    }
  }, [value]);

  // ✅ 3. Sync ค่าจาก Grid (กรณี Grid ถูก Reset จากที่อื่นที่ไม่ใช่ Component นี้)
  useEffect(() => {
    // ถ้าใน Grid ว่างเปล่า แต่ใน Input ยังมีค่า -> แสดงว่าถูก Clear จากที่อื่น -> ให้ล้าง Input ตาม
    if (
      (!quickFilterValues || quickFilterValues.length === 0) &&
      searchText !== ""
    ) {
      setSearchText("");
    }
  }, [quickFilterValues]); // เช็คเฉพาะตอน values เปลี่ยน

  // Update Animation state เมื่อ searchText เปลี่ยน
  useEffect(() => {
    if (searchText) {
      setInputOpen(true);
    }
  }, [searchText]);

  const onMouseEnter = () => {
    setInputOpen(true);
  };

  const onBlur = (event: any) => {
    if (event.target.value === "" && !searchText) {
      setInputOpen(false);
    }
  };

  const handleClear = () => {
    // 1. ล้างค่าใน Grid
    apiRef.current.setQuickFilterValues([]);
    // 2. ล้างค่าใน Local State
    setSearchText("");

    // 3. แจ้ง Parent
    if (onChange) {
      // ✅ แก้ไข: เพิ่ม currentTarget เพื่อรองรับ Parent ที่เรียกใช้ event.currentTarget.value
      onChange({
        target: { value: "" },
        currentTarget: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // if (onChange) {
    //   onChange({
    //     target: { value: "" },
    //   } as React.ChangeEvent<HTMLInputElement>);
    // }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // ✅ 4. อัปเดต Local State ทันที (เก็บ Space ไว้)
    setSearchText(newValue);

    // ส่งค่าต่อไปให้ Parent (ถ้ามี)
    if (onChange) {
      onChange(event);
    }

    // อัปเดต Filter ของ DataGrid (ตัด Space ทิ้งเฉพาะตอนส่งเข้า Grid Logic)
    apiRef.current.setQuickFilterValues(
      newValue.split(" ").filter((word: string) => word !== ""),
    );
  };

  return (
    <GridToolbarContainer
      sx={{
        padding: 0,
        width: { xs: "100%", sm: "auto" },
      }}
    >
      <GridToolbarQuickFilter
        variant="outlined"
        // ✅ 5. Bind กับ searchText (Local State) แทน visualValue เดิม
        value={searchText}
        onChange={handleChange}
        {...other}
        sx={{
          margin: 0,
          padding: 0,
          height: "40px",
          transition: ".5s",
          // ใช้ searchText คำนวณความกว้าง
          width: inputOpen || searchText ? "100%" : "50px",
        }}
        inputProps={{
          style: {
            padding: 0,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <img src="/img/search-normal.svg" alt="search" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="close"
                onClick={handleClear}
                sx={{
                  visibility: searchText ? "visible" : "hidden",
                }}
              >
                <CloseIcon
                  sx={{
                    fontSize: "18px",
                  }}
                />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            padding: "7px 8px 8px 8px",
            margin: 0,
          },
        }}
        onMouseEnter={onMouseEnter}
        onBlur={onBlur}
        id="GridToolbarQuickFilter"
        debounceMs={200}
      />
    </GridToolbarContainer>
  );
};
